import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';

import { MethodTypeAcquisitionField } from './method-type.state';

/**
 * A staged method-type draft always carries its id: the client temp id
 * (`tmp-<uuid>`) for method types created in the UI, the real DB id for edits to
 * persisted method types. It captures the editor's draft slices plus the asset
 * type display name (so a re-opened edit can restore the asset-type selector
 * without a DB round-trip).
 *
 * Mirrors `StagedPipelineDraft` (`SavePipelineInput & { id }`). There is no
 * `SaveMethodTypeInput` shape that fully matches the editor draft yet, so this
 * declares the draft slices explicitly; the future case-type save cascade reads
 * them to drive the existing method-type save command.
 */
export interface StagedMethodTypeDraft {
  id: string;
  name: string;
  assetTypeId: string;
  assetTypeName?: string;
  sampleNames: Record<string, string>[];
  config?: Record<string, unknown>;
  acquisitionFields: MethodTypeAcquisitionField[];
}

/*
 * Local edit applied to a method type that DOES exist in the database.
 * `original` is the pre-edit catalog summary; the listing flow needs it to hide
 * the stale DB row, place the edited version at its (re-sorted) position, and
 * keep the total count exact even when the edit moves the method type in/out of
 * the active search match. `draft` is the full edit payload, keyed onto
 * `original.id`.
 */
export interface StagedMethodTypeOverride {
  original: { id: string; name: string };
  draft: StagedMethodTypeDraft;
}

interface StagedMethodTypesStateModel {
  /*
   * Full drafts of method types created in the UI (via the editor dialog) but
   * not yet persisted, each carrying its client temp id. Source of truth — the
   * catalog's items adapter is just a per-page snapshot that may surface some of
   * these. They stay listed in the catalog for the rest of the session even when
   * not assigned to the case type; the save cascade will only persist the ones
   * that are still an active part of the protocol.
   */
  pendingMethodTypes: StagedMethodTypeDraft[];

  /*
   * Local edits to persisted method types, keyed by id. Until persisted, the
   * listing flow shows the (projected) override in place of the DB row sharing
   * this id.
   */
  methodTypeOverrides: Record<string, StagedMethodTypeOverride>;

  /** True while the save cascade persists the staged drafts. */
  saving: boolean;

  /*
   * Per-row failures of the last consolidation pass, keyed by the staged id
   * (client temp id for pending drafts, DB id for overrides) — set by the
   * cascade, `StagedPipelinesState.saveExceptions` parity.
   */
  saveExceptions: Record<string, Error>;
}

const INITIAL_STATE: StagedMethodTypesStateModel = {
  pendingMethodTypes: [],
  methodTypeOverrides: {},
  saving: false,
  saveExceptions: {},
};

/**
 * Shared method-type staging ledger. One instance is provided by the outermost
 * consumer (the case-type editor page, alongside `AssignedMethodTypesState`) and
 * shared down the dialog stack: drafts survive a manager unmount, and the
 * case-type editor provides the single shared instance the future save cascade
 * reads (mirroring `StagedPipelinesState` / `StagedResourcesState`).
 *
 * Deliberately NOT part of `methodTypeListStoreProviders` /
 * `methodTypeManagerProviders`: a per-manager copy would be torn down with each
 * manager and could not survive the dialog stack.
 */
@Injectable()
export class StagedMethodTypesState extends ComponentStore<StagedMethodTypesStateModel> {
  constructor() {
    super({ ...INITIAL_STATE });
  }

  // ============================================================================
  // Selectors
  // ============================================================================

  public readonly pendingMethodTypes$ = this.select((state) => state.pendingMethodTypes);
  public readonly methodTypeOverrides$ = this.select((state) => state.methodTypeOverrides);
  public readonly saving$ = this.select((state) => state.saving);
  public readonly saveExceptions$ = this.select((state) => state.saveExceptions);

  // ============================================================================
  // Updaters
  // ============================================================================

  /**
   * Upsert the full draft of a not-yet-persisted method type (keyed by its
   * client temp id). Drafts without an id are ignored.
   */
  public readonly stagePending = this.updater((state, draft: StagedMethodTypeDraft) => {
    const id = draft.id;
    if (!id) return state;
    const without = state.pendingMethodTypes.filter((p) => p.id !== id);
    return { ...state, pendingMethodTypes: [...without, { ...draft, id }] };
  });

  /**
   * Record the full draft of a local edit to a persisted method type (keyed by
   * `original.id`; the draft id is normalized onto it).
   */
  public readonly stageOverride = this.updater(
    (state, entry: { original: { id: string; name: string }; draft: StagedMethodTypeDraft }) => {
      const id = entry.original.id;
      const draft: StagedMethodTypeDraft = { ...entry.draft, id };
      return {
        ...state,
        methodTypeOverrides: { ...state.methodTypeOverrides, [id]: { original: entry.original, draft } },
      };
    }
  );

  /** Remove a method type's staged entry, whichever slot it occupies. */
  public readonly unstage = this.updater((state, id: string) => {
    const hasPending = state.pendingMethodTypes.some((p) => p.id === id);
    const hasOverride = id in state.methodTypeOverrides;
    if (!hasPending && !hasOverride) return state;
    const methodTypeOverrides = { ...state.methodTypeOverrides };
    delete methodTypeOverrides[id];
    return {
      ...state,
      pendingMethodTypes: state.pendingMethodTypes.filter((p) => p.id !== id),
      methodTypeOverrides,
    };
  });

  /** Drop everything staged this session. */
  public readonly clear = this.updater(() => ({ ...INITIAL_STATE }));

  /**
   * After persistence, swap client temp ids for the REAL ids the saves
   * returned (`AssignedPipelinesState.remapIds` parity). A remapped pending
   * draft becomes the real-id entry; ids absent from the map stay put. Eager —
   * keeps the staging consistent so a later cascade-phase failure + retry still
   * points at real ids.
   */
  public readonly remapIds = this.updater((state, idMap: Record<string, string>) => {
    if (Object.keys(idMap).length === 0) return state;
    const pendingMethodTypes = state.pendingMethodTypes.map((draft) => {
      const newId = idMap[draft.id];
      return newId ? { ...draft, id: newId } : draft;
    });
    const methodTypeOverrides: Record<string, StagedMethodTypeOverride> = {};
    for (const id of Object.keys(state.methodTypeOverrides)) {
      const newId = idMap[id] ?? id;
      const entry = state.methodTypeOverrides[id];
      methodTypeOverrides[newId] = {
        original: { ...entry.original, id: newId },
        draft: { ...entry.draft, id: newId },
      };
    }
    return { ...state, pendingMethodTypes, methodTypeOverrides };
  });

  // ============================================================================
  // Updaters — cascade bookkeeping (StagedPipelinesState parity)
  // ============================================================================

  public readonly setSaving = this.updater((state, saving: boolean) => ({ ...state, saving }));

  // Record one row's save failure keyed by its staged id. Does NOT touch
  // `saving` — the cascade flips it once after the batch settles.
  public readonly recordSaveException = this.updater((state, entry: { id: string; error: Error }) => ({
    ...state,
    saveExceptions: { ...state.saveExceptions, [entry.id]: entry.error },
  }));

  // Clear all recorded save failures. No-op when already empty so a fresh `{}`
  // reference can't fire a spurious selector emit.
  public readonly clearSaveExceptions = this.updater((state) =>
    Object.keys(state.saveExceptions).length === 0 ? state : { ...state, saveExceptions: {} }
  );
}
